import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FiMessageSquare, FiSend, FiX } from 'react-icons/fi';

const POLL_MS = 20000;
const seenKey = (workspaceId) => `easten-client-chat-seen-${workspaceId}`;
const readSeen = (workspaceId) => { try { return Number(window.localStorage.getItem(seenKey(workspaceId))) || 0; } catch { return 0; } };

export function hasUnreadClientMessage(messages, seenAt) {
  return messages.some((item) => item.senderRole === 'client' && new Date(item.createdAt).getTime() > seenAt);
}

// Header icon + popover: read and reply to the clients assigned to the current workspace.
export default function ClientMessages({ apiBase, workspaceId }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [seenAt, setSeenAt] = useState(() => readSeen(workspaceId));
  const root = useRef(null);
  const thread = useRef(null);
  const request = { withCredentials: true };

  const load = useCallback(async () => {
    if (!workspaceId || String(workspaceId).startsWith('workspace-')) return;
    try {
      const { data } = await axios.get(`${apiBase}/api/chat/workspaces/${workspaceId}`, { withCredentials: true });
      setMessages(data.messages || []);
      setError('');
    } catch (failure) {
      if (failure.response?.status !== 404) setError('Messages could not be refreshed.');
    }
  }, [apiBase, workspaceId]);

  useEffect(() => { setMessages([]); setSeenAt(readSeen(workspaceId)); }, [workspaceId]);
  useEffect(() => {
    load();
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') load(); }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape' || (event.type === 'pointerdown' && !root.current?.contains(event.target))) setOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', close); };
  }, []);
  useEffect(() => {
    if (!open) return;
    const now = Date.now();
    try { window.localStorage.setItem(seenKey(workspaceId), String(now)); } catch { /* seen marker is optional */ }
    setSeenAt(now);
    thread.current?.scrollTo({ top: thread.current.scrollHeight });
  }, [open, messages.length, workspaceId]);

  const send = async () => {
    const message = reply.trim();
    if (!message || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${apiBase}/api/chat/workspaces/${workspaceId}`, { message }, request);
      setMessages((current) => [...current, data.message]);
      setReply('');
      setError('');
    } catch (failure) {
      setError(failure.response?.data?.error || 'Message could not be sent.');
    } finally {
      setSending(false);
    }
  };

  const unread = !open && hasUnreadClientMessage(messages, seenAt);
  return (
    <div className="client-messages-wrap" ref={root}>
      <button className="command-plain-icon" type="button" aria-label="Client messages" aria-expanded={open} title="Client messages" onClick={() => setOpen((value) => !value)}>
        <FiMessageSquare aria-hidden="true" />
        {unread && <span className="notification-dot" />}
      </button>
      {open && <section className="client-messages-pop" aria-label="Client messages">
        <header><div><strong>Client messages</strong><small>Chat with the clients assigned to this workspace</small></div><button type="button" aria-label="Close client messages" onClick={() => setOpen(false)}><FiX /></button></header>
        <div className="client-messages-thread" ref={thread}>
          {messages.slice(-40).map((item) => <article className={item.senderRole === 'client' ? 'theirs' : 'mine'} key={item.id}>
            <div><strong>{item.senderName}</strong><time>{new Date(item.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</time></div>
            <p>{item.message}</p>
          </article>)}
          {!messages.length && <p className="client-messages-empty">No client messages yet. When a client writes from their dashboard, it shows up here.</p>}
        </div>
        {error && <p className="client-messages-error" role="alert">{error}</p>}
        <footer>
          <textarea aria-label="Reply to client" maxLength={2000} placeholder="Reply to clients…" value={reply} onChange={(event) => setReply(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) send(); }} />
          <button className="btn primary" type="button" disabled={!reply.trim() || sending} onClick={send}><FiSend />{sending ? 'Sending' : 'Send'}</button>
        </footer>
      </section>}
    </div>
  );
}
